import { useCallback, useEffect, useState } from 'react';
import { Map } from 'immutable';

interface WebRtcPeer {
    peerConnection?: RTCPeerConnection;
    getRemoteStream?: () => MediaStream | null;
    isEmpty?: () => boolean;
}

/**
 * Resolves a remote presenter's shared-screen stream for the on-stage tile.
 *
 * A presenter carries their screen on `primaryPeer` (their camera rides on
 * `secondaryPeer`, rendered separately as a strip face). The peer hands its
 * MediaStream over imperatively via `getRemoteStream()`, and that stream can
 * swap when the connection re-establishes — so we watch the peer's connection
 * state and re-resolve the stream on every transition, exactly as the remote
 * camera tile used to before this was hoisted out of it.
 *
 * Returns the current `stream` (null until it lands) plus a `sourceKey` (the
 * peer) that the consuming tile keys its imperative <video> attach and aspect
 * reporting on, so a late-arriving or reconnecting stream is re-attached.
 */
const useRemoteScreenShareStream = (
    videoMember: Map<string, any> | undefined,
): { stream: MediaStream | null; sourceKey: WebRtcPeer | null } => {
    const peer = (videoMember?.get('primaryPeer') as WebRtcPeer | null) ?? null;

    const [stream, setStream] = useState<MediaStream | null>(null);

    const resolveStream = useCallback(() => {
        setStream(peer?.getRemoteStream?.() ?? null);
    }, [peer]);

    useEffect(() => {
        resolveStream();
        const connection = peer?.peerConnection;
        if (!connection) {
            return;
        }
        // Chrome exposes connectionState; Firefox only iceConnectionState. Both
        // fire as the peer goes connecting -> connected -> disconnected, so we
        // re-resolve the stream (it can be replaced on reconnect) on either.
        const event =
            'connectionState' in connection
                ? 'connectionstatechange'
                : 'iceconnectionstatechange';
        connection.addEventListener(event, resolveStream);
        return () => {
            connection.removeEventListener(event, resolveStream);
        };
    }, [peer, resolveStream]);

    return { stream, sourceKey: peer };
};

export default useRemoteScreenShareStream;
